import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
	Panel, Row, Grid, Col, Button, ButtonGroup,
} from 'react-bootstrap';
import GoogleMap from 'google-map-react';
import classNames from 'classnames';
import {
	get, isNull, isEmpty, isEqual, pick, forEach,
} from 'lodash';
import moment from 'moment-timezone';
import utils, { constants } from '@utils';
import {
	Loader, MapMarker, SubNavBar, RKPayRibbon, Tooltip, ActivitiesTimeline,
} from '@components/common';
import { ExpandableContext } from '@components/contexts';
import JobActions, { thunks } from '@redux/jobs';
import JobHistoryModal from '@components/modals/jobHistoryModal/jobHistoryModal';
import PositionsPanel from './components/positionsPanel';

class View extends Component {
	constructor(props) {
		super(props);
		const {
			user,
			ownProps : { match : { params } },
			loadJobEvent,
		} = props;
		const jobId = get(params, ['id']);
		const { TimeZoneId : timeZone } = user;

		this.state = {
			timeZone,
			actionPermissions : {},
			jobEvent          : {},
		};

		loadJobEvent(jobId);
	}

	static getDerivedStateFromProps(nextProps, lastState) {
		const newState = {};
		const timeZone = nextProps.user.TimeZoneId;

		if (!isEqual(timeZone, lastState.timeZone)) newState.timeZone = timeZone;

		if (!isEqual(nextProps.jobEvent, lastState.jobEvent)) {
			newState.jobEvent = nextProps.jobEvent;
			newState.actionPermissions = utils.getJobActionPermissions(nextProps.user, nextProps.jobEvent);
		}

		return !isEmpty(newState) ? newState : null;
	}

	// clear job event data on unmount
	componentWillUnmount = () => {
		const { clearJobEvent } = this.props;

		clearJobEvent();
	}

	shouldComponentUpdate = ({
		jobEvent,
		isLoading,
		notes,
		notesLoader,
	}, {
		timeZone,
		actionPermissions,
	}) => {
		const lastProps = this.props;
		const lastState = this.state;

		return (
			!isEqual(jobEvent, lastProps.jobEvent)
			|| !isEqual(notes, lastProps.notes)
			|| !isEqual(notesLoader, lastProps.notesLoader)
			|| !isEqual(isLoading, lastProps.isLoading)
			|| !isEqual(timeZone, lastState.timeZone)
			|| !isEqual(actionPermissions, lastState.actionPermissions)
		);
	}

	onOpenHistoryModal = (event) => {
		event.preventDefault();
		const { setModalOpen, setModalSettings, jobEvent : { Id } } = this.props;

		setModalSettings('jobHistoryModal', {
			showDetails : false,
			jobId       : Id,
		});
		setModalOpen('jobHistoryModal', true);
	}

	onClickBackToJobs = (event) => {
		event.preventDefault();

		const { history : { push } } = this.props;
		push('/jobs');
	}

	getPostedStatus = () => {
		const {
			jobEvent: {
				Posted, PostedType, JobAutoAssignType, PostedDate, PostAtDateTime, PostedGroupName,
			},
		} = this.props;
		const { timeZone } = this.state;
		let postedStatus = 'Not Posted';
		let autoAssignType = 'None';
		const now = moment().utc().format();

		if (
			Posted && (
				PostedType === constants.JOB_POSTED_TYPES.ALL
				|| PostedType === constants.JOB_POSTED_TYPES.ALL_MINUTES_CONSTRAINT
				|| PostedType === constants.JOB_POSTED_TYPES.ALL_WEEKDAY_CONSTRAINT
			)
		) postedStatus = now > (PostAtDateTime || PostedDate) ? 'Posted' : 'Will be posted on';

		if (
			Posted && (
				PostedType === constants.JOB_POSTED_TYPES.ALL_GROUPS
			)
		) postedStatus = now > (PostAtDateTime || PostedDate) ? 'Posted to All Officer Groups' : 'Will be posted to All Officer Groups on';

		if (
			Posted && (
				PostedType === constants.JOB_POSTED_TYPES.GROUPS
			)
		) postedStatus = now > (PostAtDateTime || PostedDate) ? `Posted to group: ${PostedGroupName}` : `Will be posted to group: ${PostedGroupName} on`;

		if (PostAtDateTime || PostedDate) {
			const space = now > PostAtDateTime ? ', ' : ' ';
			postedStatus += space + utils.parseUTCDateToTimeZone(PostAtDateTime || PostedDate, timeZone).format('MM/DD/YYYY h:mm A');
		}

		if (Posted) {
			switch (JobAutoAssignType) {
				case 2:
					autoAssignType = 'Auto-Assign: First come, first serve';
					break;
				case 3:
					autoAssignType = 'Auto-Assign: Number of hours';
					break;
				case 4:
					autoAssignType = 'Auto-Assign: Number of jobs';
					break;
				default:
					autoAssignType = '';
					break;
			}
		}

		return (
			<Fragment>
				{ postedStatus }
				<If condition={Posted && autoAssignType}>
					<br />
					{ autoAssignType }
				</If>
			</Fragment>
		);
	}

	getAdditionalAgencyContacts = () => {
		const {
			jobEvent : { SecondarySupervisorModel },
		} = this.props;

		let supervisors = '';

		if (isEmpty(SecondarySupervisorModel)) supervisors = 'Unspecified';
		else {
			const arrayLength = SecondarySupervisorModel.length;

			forEach(SecondarySupervisorModel, (item, index) => {
				const newName = `${item.SecondaryAgencySupervisorName}${index !== arrayLength - 1 ? ', ' : ''}`;
				supervisors = `${supervisors}${newName}`;
			});
		}

		return (
			<Fragment>
				<span className="color-gray-dove">{ supervisors }</span>
			</Fragment>
		);
	}

	getLocationDetails = () => {
		const {
			jobEvent : {
				LocationLatitude : latitude,
				LocationLongitude : longitude,
				LocationAddress : address,
				LocationCity : city,
				LocationState : state,
				LocationZipcode : zip,
			},
		} = this.props;

		return utils.concatenateAddressDetails({
			address,
			city,
			state,
			zip,
			latitude,
			longitude,
		});
	}

	getEventDate = () => {
		const {
			jobEvent : { StartDateTime, EndDateTime, TimeZoneId },
		} = this.props;
		const { timeZone } = this.state;

		const jobTimeZoneId = isNull(TimeZoneId) ? timeZone : TimeZoneId;

		const startTimeInJobTimeZone = utils.parseUTCDateToTimeZone(StartDateTime, jobTimeZoneId);
		const endTimeInJobTimeZone = utils.parseUTCDateToTimeZone(EndDateTime, jobTimeZoneId);
		const startTimeInUserTimeZone = utils.parseUTCDateToTimeZone(StartDateTime, timeZone);
		const endTimeInUserTimeZone = utils.parseUTCDateToTimeZone(EndDateTime, timeZone);

		return (
			<Fragment>
				{ startTimeInJobTimeZone.format('MM/DD/YYYY') }
				<br />
				{ startTimeInJobTimeZone.format('h:mm A') }
				{' '}
				-
				{ endTimeInJobTimeZone.format('h:mm A') }
				{' '}
				{moment.tz(jobTimeZoneId).zoneAbbr()}
				{ /* Only display date in current time zone, if the user timezone is different that the  job timezone */ }
				<If condition={timeZone !== jobTimeZoneId}>
					<br />
					<br />
					{ startTimeInUserTimeZone.format('h:mm A') }
					{' '}
					-
					{ endTimeInUserTimeZone.format('h:mm A') }
					{' '}
					{moment.tz(timeZone).zoneAbbr()}
					{' '}
					(Local Timezone)
				</If>
			</Fragment>
		);
	}

	renderJurisdictions = (jurisdictions) => (
		<Fragment>
			<Choose>
				<When condition={jurisdictions.length > 0}>
					<For
						each="item"
						index="index"
						of={jurisdictions}
					>
						<Row
							key={`job_view_jurisdictions_${item.JurisdictionId}`}
							className="margin-bottom-10"
						>
							<Col md={8}>
								<span className="color-gray-dove">{ item.JurisdictionName }</span>
							</Col>
							<Col md={4}>
								<span className="color-gray-dove">
									<span>Expires: </span>
									<Choose>
										<When condition={isNull(item.ExpiresBefore)}>
											<span>Never</span>
										</When>
										<Otherwise>
											<span>
												{ get(constants.JURISDICTION_EXPIRES_OPTIONS.find((i) => i.value === item.ExpiresBefore), 'label', '') }
											</span>
										</Otherwise>
									</Choose>
								</span>
							</Col>
						</Row>
					</For>
				</When>
				<Otherwise>
					<span>Unspecified</span>
				</Otherwise>
			</Choose>
		</Fragment>
	)

	showOfficerRate = () => {
		const { jobEvent : { MinRate, MaxRate } } = this.props;
		if (MinRate === MaxRate) return utils.currencyFormatter(MinRate);
		return `${utils.currencyFormatter(MinRate)} - ${utils.currencyFormatter(MaxRate)}`;
	}

	render() {
		const {
			jobEvent,
			isLoading,
			notes,
			notesLoader,
			user,
		} = this.props;
		const {
			actionPermissions : {
				allowViewHistory,
			},
		} = this.state;

		if (isEmpty(jobEvent)) return <Loader active />;

		const center = {
			lat : parseFloat(jobEvent.LocationLatitude),
			lng : parseFloat(jobEvent.LocationLongitude),
		};

		return (
			<React.Fragment>
				<SubNavBar
					backText="Back to job list"
					onClickBack={this.onClickBackToJobs}
				>
					<ButtonGroup>
						<If condition={allowViewHistory}>
							<Tooltip content="View Job History">
								<button
									type="button"
									id="view-job-history"
									aria-label="view job history"
									className="btn bg-transparent"
									onClick={this.onOpenHistoryModal}
								>
									<i className="rkf rkf-clock-revert" />
								</button>
							</Tooltip>
						</If>
					</ButtonGroup>
				</SubNavBar>
				<Grid className="padded">
					<Col md={12}>
						<h3 className="margin-top-0">Job Details</h3>
						<div className="details-panel details-panel__main-container margin-top-0">
							<div>
								<div className="details-panel__main-content flex-align-items-center">
									<div className="flex-1">
										<p className="color-gray-catskill-white font-size-14 margin-bottom-0">
											#
											{' '}
											{ jobEvent.Id }
										</p>
										<h4 className="font-semi-bold font-size-18 margin-bottom-0 margin-top-4">
											{ jobEvent.Name }
										</h4>
										<p className="font-size-14 margin-bottom-0 margin-top-4">
											<span className="color-gray-catskill-white">{ jobEvent.ClientName }</span>
										</p>
									</div>
									<div className="flex flex-0">
										<div className="text-center">
											<h4 className="font-semi-bold font-weight-600 font-size-18 margin-bottom-5 margin-top-0">
												{ jobEvent.ApplicantsNeeded }
											</h4>
											<p className="color-gray-catskill-white font-size-12 margin-bottom-0">Needed</p>
										</div>
										<div className="text-center padding-left-30">
											<h4 className="font-semi-bold font-weight-600 font-size-18 margin-bottom-5 margin-top-0">
												{ jobEvent.ApplicantsApplied }
											</h4>
											<p className="color-gray-catskill-white font-size-12 margin-bottom-0">Applicants</p>
										</div>
										<div className="text-center padding-left-30">
											<h4 className="font-semi-bold font-weight-600 font-size-18 margin-bottom-5 margin-top-0">
												{ jobEvent.ApplicantsAssigned }
											</h4>
											<p className="color-gray-catskill-white font-size-12 margin-bottom-0">Assigned</p>
										</div>
									</div>
								</div>
								<div className="details-panel__main-content">
									<div className="flex flex-1 padding-left-15 padding-right-15">
										<i className="rkf rkf-calendar" />
										<div>
											<h5 className="color-gray-catskill-white font-semi-bold font-weight-600 margin-bottom-2 margin-top-0">
												Event Date
											</h5>
											<p className="font-size-14">
												{' '}
												{ this.getEventDate() }
												{' '}
											</p>
										</div>
									</div>
									<div className="flex flex-1 padding-left-15 padding-right-15">
										<i className="rkf rkf-dollar" />
										<div>
											<h5 className="color-gray-catskill-white font-semi-bold font-weight-600 margin-bottom-2 margin-top-0">
												Officer Rate
											</h5>
											<p className="font-size-14 margin-bottom-0">
												{ this.showOfficerRate() }
											</p>
										</div>
									</div>
									<div className="flex flex-1 padding-left-15 padding-right-15">
										<i className="rkf rkf-clock" />
										<div>
											<h5 className="color-gray-catskill-white font-semi-bold font-weight-600 margin-bottom-2 margin-top-0">Hours</h5>
											<p className="font-size-14 margin-bottom-0">
												{ jobEvent.ScheduledHours }
												{' '}
												Hours
											</p>
										</div>
									</div>
									<div className="flex flex-1 padding-right-15">
										<i className={classNames('rkf',
											{ 'rkf-eye color-posted' : jobEvent.Posted, 'rkf-eye-hidden color-unposted' : !jobEvent.Posted })}
										/>
										<div>
											<h5 className="color-gray-catskill-white font-semi-bold font-weight-600 margin-bottom-2 margin-top-0">
												Posted Status
											</h5>
											<p className="font-size-14 margin-bottom-0">
												{ this.getPostedStatus() }
											</p>
										</div>
									</div>
								</div>
								<div className="details-panel__main-content flex-direction-column">
									<h5 className="font-semi-bold font-weight-600 margin-bottom-2 margin-top-0">Project</h5>
									<p className="font-weight-400 margin-bottom-20 color-gray-dove">
										{ jobEvent.ProjectName }
									</p>
									<h5 className="font-semi-bold font-weight-600 margin-bottom-2 margin-top-0">Type</h5>
									<p className="font-weight-400 margin-bottom-20 color-gray-dove">
										{ jobEvent.JobEventTypeName ? jobEvent.JobEventTypeName : 'Not Specified' }
									</p>
									<h5 className="font-semi-bold font-weight-600 margin-bottom-2 margin-top-0">Description</h5>
									<p className="font-weight-400 margin-bottom-20 color-gray-dove">
										{ jobEvent.Details }
									</p>
								</div>
							</div>
							<If condition={jobEvent.IsRKPay}>
								<RKPayRibbon />
							</If>
						</div>
					</Col>
					<Col md={8}>
						<PositionsPanel />

						<Panel className="details-panel">
							<Panel.Heading>
								Job Contact
							</Panel.Heading>
							<Panel.Body className="padding-vertical-2em">
								<Row className="margin-bottom-25">
									<Col md={5}>
										<strong> Client Contact: </strong>
									</Col>
									<Col md={7}>
										{ isNull(jobEvent.ClientSupervisorName) ? 'Unspecified' : jobEvent.ClientSupervisorName }
									</Col>
								</Row>
								<Row className="margin-bottom-25">
									<Col md={5}>
										<strong> Client Contact Phone: </strong>
									</Col>
									<Col md={7}>
										{ isNull(jobEvent.ClientSupervisorMobilePhone)
											? 'Unspecified'
											: utils.formatPhoneNumber(jobEvent.ClientSupervisorMobilePhone) }
									</Col>
								</Row>
								<Row className="margin-bottom-25">
									<Col md={5}>
										<strong> Agency Contact: </strong>
									</Col>
									<Col md={7}>
										{ isNull(jobEvent.SupervisorName) ? 'Unspecified' : jobEvent.SupervisorName }
									</Col>
								</Row>
								<Row className="margin-bottom-25">
									<Col md={5}>
										<strong> Agency Contact Phone: </strong>
									</Col>
									<Col md={7}>
										{ isNull(jobEvent.SupervisorMobilePhone) ? 'Unspecified' : utils.formatPhoneNumber(jobEvent.SupervisorMobilePhone) }
									</Col>
								</Row>
								<Row className="margin-bottom-25">
									<Col md={5}>
										<strong> Additional Agency Contacts: </strong>
									</Col>
									<Col md={7}>
										{ this.getAdditionalAgencyContacts() }
									</Col>
								</Row>
							</Panel.Body>
						</Panel>

						<Panel className="details-panel">
							<Panel.Heading>
								Job Requirements
							</Panel.Heading>
							<Panel.Body className="padding-vertical-2em">
								<Row className="margin-bottom-25">
									<Col md={5}>
										<span className="font-semi-bold"> Agency Requirement: </span>
									</Col>
									<Col md={7}>
										{ this.renderJurisdictions(jobEvent.RequiredJurisdictions) }
									</Col>
								</Row>
								<Row className="margin-bottom-25">
									<Col md={5}>
										<span className="font-semi-bold"> Excluded Agency: </span>
									</Col>
									<Col md={7}>
										{ this.renderJurisdictions(jobEvent.ExcludedJurisdictions) }
									</Col>
								</Row>
							</Panel.Body>
						</Panel>

						<Panel className="details-panel">
							<Panel.Heading>
								Payment Details
							</Panel.Heading>
							<Panel.Body className="padding-vertical-2em">
								<Row className="margin-bottom-25">
									<Col md={5}>
										<strong> RKPay Enabled: </strong>
									</Col>
									<Col md={7}>
										{ jobEvent.IsRKPay ? 'Yes' : 'No' }
									</Col>
								</Row>
								<Row className="margin-bottom-25">
									<Col md={5}>
										<strong>Requires Prepayment:</strong>
									</Col>
									<Col md={7}>
										<span className="color-gray-shuttle">{jobEvent.RequirePrepayment ? 'Yes' : 'No'}</span>
									</Col>
								</Row>
								<Row className="margin-bottom-25">
									<Col md={5}>
										<strong> Payment Details: </strong>
									</Col>
									<Col md={7}>
										{ jobEvent.IsRKPay ? 'Via RKPay' : jobEvent.PaymentMethod }
									</Col>
								</Row>
							</Panel.Body>
						</Panel>
					</Col>
					<Col md={4}>
						<div className="details-panel details-panel__sided">
							<Panel>
								<Panel.Heading className="flex flex-align-items-center">
									<i className="rkf rkf-map-pin" />
									<div>
										<If condition={!!jobEvent.LocationName}>
											<h4 className="font-semi-bold font-weight-600 font-size-16 margin-top-0 margin-bottom-3">
												{ jobEvent.LocationName }
											</h4>
										</If>
										<p className="font-size-12 font-weight-400 color-gray-santas margin-0">
											{ this.getLocationDetails() }
										</p>
									</div>
								</Panel.Heading>
								<Panel.Body className="map-container">
									<GoogleMap
										defaultZoom={15}
										center={center}
										bootstrapURLKeys={{ key : constants.GOOGLE_MAP_SETTINGS.key }}
										options={constants.GOOGLE_MAP_OPTIONS}
									>
										<MapMarker
											lat={center.lat}
											lng={center.lng}
											type="job"
										/>
									</GoogleMap>
								</Panel.Body>
							</Panel>
							<ExpandableContext.Provider initiallyExpanded>
								<ExpandableContext.Consumer>
									{({ expanded, onToggle }) => (
										<Panel
											expanded={expanded}
											onToggle={() => {}}
											className="position-relative"
										>
											<Panel.Heading>
												<Button
													onClick={onToggle}
													className="details-panel__sided__heading-button"
												>
													<div className="flex flex-align-items-center flex-1">
														<i className="rkf rkf-timeline color-gray-dove font-size-25 margin-right-15" />
														<h4 className="font-semi-bold font-weight-600 font-size-16 margin-0">
															Activities Timeline
														</h4>
													</div>
													<i
														className={classNames('rkf rkf-chevron-down color-blue-azure-radiance transition-transform', {
															'transform-rotate-180' : expanded,
														})}
													/>
												</Button>
											</Panel.Heading>
											<Panel.Collapse>
												<Panel.Body>
													<ActivitiesTimeline
														notes={notes}
														timeZone={user.TimeZoneId}
														isLoading={notesLoader}
													/>
												</Panel.Body>
											</Panel.Collapse>
										</Panel>
									)}
								</ExpandableContext.Consumer>
							</ExpandableContext.Provider>
						</div>
					</Col>
				</Grid>
				<JobHistoryModal />
				<Loader active={isLoading} />
			</React.Fragment>
		);
	}
}

const mapStateToProps = ({
	app  : {
		user,
	},
	jobs : {
		jobEvent,
		notes   : { ItemList : notes = [] },
		loaders : { details : isLoading, notes : notesLoader },
	},
},
ownProps) => ({
	jobEvent,
	isLoading,
	user,
	notes,
	notesLoader,
	ownProps,
});

const mapDispatchToProps = (dispatch) => ({
	...pick(bindActionCreators(JobActions, dispatch), ['setModalOpen', 'setModalSettings', 'clearJobEvent']),
	...pick(bindActionCreators(thunks, dispatch), ['loadJobEvent']),
});

View.propTypes = {
	clearJobEvent    : PropTypes.func.isRequired,
	history          : PropTypes.object.isRequired,
	isLoading        : PropTypes.bool,
	jobEvent         : PropTypes.object,
	loadJobEvent     : PropTypes.func.isRequired,
	notes            : PropTypes.array,
	notesLoader      : PropTypes.bool,
	setModalOpen     : PropTypes.func.isRequired,
	setModalSettings : PropTypes.func.isRequired,
	user             : PropTypes.object,
	ownProps         : PropTypes.object.isRequired,
};

View.defaultProps = {
	isLoading   : false,
	jobEvent    : {},
	notes       : [],
	notesLoader : false,
	user        : {},
};

export default connect(mapStateToProps, mapDispatchToProps)(View);
